import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
  Badge,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
// import HomeIcon from '@mui/icons-material/Home';
import EventIcon from '@mui/icons-material/Event';
import PeopleIcon from '@mui/icons-material/People';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { getCurrentUser } from '../lib/api-client';
import { useMatchNotification } from '../context/MatchNotificationContext';

// Yellow dot badge for new matches
const MatchBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#ffc107',
    color: '#ffc107',
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    width: 12,
    height: 12,
    borderRadius: '50%',
    minWidth: 12,
  },
}));

const navItems = [
  // { label: 'Home', path: '/', icon: <HomeIcon /> },
  { label: 'Events', path: '/events', icon: <EventIcon /> },
  { label: 'Find Partners', path: '/discover', icon: <SearchIcon /> },
  { label: 'Users', path: '/users', icon: <PeopleIcon /> },
  { label: 'My Profile', path: '/profile', icon: <PersonIcon /> },
];

const adminItems = [
  { label: 'Events Admin', path: '/events-admin', icon: <AdminPanelSettingsIcon /> },
];

export const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const currentUser = getCurrentUser();
  const { hasNewMatch, clearMatchBadge } = useMatchNotification();

  const toggleDrawer = (open: boolean) => () => {
    setDrawerOpen(open);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const handleProfileClick = () => {
    // Clear badge when navigating to profile (will clear when Requests tab is visited)
    navigate('/profile?view=requests');
    clearMatchBadge();
  };

  const userInitial =
    currentUser?.displayName?.charAt(0).toUpperCase() ||
    currentUser?.email?.charAt(0).toUpperCase() ||
    '?';

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: '#fff',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          {/* Hamburger Menu */}
          <IconButton
            edge="start"
            aria-label="menu"
            onClick={toggleDrawer(true)}
            sx={{ color: '#2c2c2c' }}
          >
            <MenuIcon />
          </IconButton>

          {/* Logo / Title */}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              cursor: 'pointer',
            }}
            onClick={() => navigate('/')}
          >
            WeMove
          </Typography>

          {/* User Profile Avatar */}
          <IconButton onClick={handleProfileClick} sx={{ p: 0 }}>
            <MatchBadge
              overlap="circular"
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              variant="dot"
              invisible={!hasNewMatch}
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  fontSize: '1rem',
                }}
              >
                {userInitial}
              </Avatar>
            </MatchBadge>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Spacer to push content below fixed AppBar */}
      <Toolbar />

      {/* Navigation Drawer */}
      <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)}>
        <Box sx={{ width: 280 }} role="presentation">
          {/* Drawer Header */}
          <Box
            sx={{
              p: 3,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#fff',
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              WeMove
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              Find your dance partner
            </Typography>
          </Box>

          {/* Navigation Items */}
          <List sx={{ pt: 1 }}>
            {navItems.map((item) => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  onClick={() => handleNavigation(item.path)}
                  selected={location.pathname === item.path}
                  sx={{
                    py: 1.5,
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(102, 126, 234, 0.1)',
                      borderRight: '3px solid #667eea',
                      '&:hover': {
                        backgroundColor: 'rgba(102, 126, 234, 0.15)',
                      },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{ color: location.pathname === item.path ? '#667eea' : 'inherit' }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontWeight: location.pathname === item.path ? 600 : 400,
                      color: location.pathname === item.path ? '#667eea' : 'inherit',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          <Divider sx={{ my: 1 }} />

          {/* Admin Section */}
          <Typography
            variant="overline"
            sx={{ px: 2, color: 'text.secondary', display: 'block', mt: 1 }}
          >
            Admin
          </Typography>
          <List>
            {adminItems.map((item) => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  onClick={() => handleNavigation(item.path)}
                  selected={location.pathname === item.path}
                  sx={{
                    py: 1.5,
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(102, 126, 234, 0.1)',
                      borderRight: '3px solid #667eea',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{ color: location.pathname === item.path ? '#667eea' : 'inherit' }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontWeight: location.pathname === item.path ? 600 : 400,
                      color: location.pathname === item.path ? '#667eea' : 'inherit',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          {/* User Info at Bottom */}
          {currentUser && (
            <>
              <Divider sx={{ mt: 'auto' }} />
              <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    }}
                  >
                    {userInitial}
                  </Avatar>
                  <Box sx={{ overflow: 'hidden' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                      {currentUser.displayName || 'User'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {currentUser.email}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Drawer>
    </>
  );
};
